import { HttpClient, HttpContext } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { filter, map, switchMap } from 'rxjs'
import { CACHE_FORCE_REFRESH, CACHING_DEFAULT_TTL, CACHING_ENABLED, CACHING_TTL } from '../config/http.config'
import { APIRequestOptions, APIResponse } from '../interfaces'
import { APIRequestResource } from '../types'
import { generateUrl } from '../util'
import { TokenService } from './token.service'

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private readonly http = inject(HttpClient)
  private readonly tokenService = inject(TokenService)

  readonly GET = <T>(
    resource: APIRequestResource,
    options: APIRequestOptions,
    caching = false,
    forceRefresh = false,
    cachingTTL = CACHING_DEFAULT_TTL
  ) => {
    return this.tokenService.isRefreshing$.pipe(
      filter((isRefreshing) => !isRefreshing),
      switchMap(() =>
        this.http.get<APIResponse<T>>(generateUrl(resource, options), {
          params: options.params,
          context: new HttpContext().set(CACHING_ENABLED, caching).set(CACHING_TTL, cachingTTL).set(CACHE_FORCE_REFRESH, forceRefresh),
        })
      )
    )
  }

  readonly GET_BLOB = (
    resource: APIRequestResource,
    options: APIRequestOptions
  ) => {
    return this.tokenService.isRefreshing$.pipe(
      filter((isRefreshing) => !isRefreshing),
      switchMap(() =>
        this.http.get(generateUrl(resource, options), {
          params: options.params,
          observe: 'response',
          responseType: 'blob'
        })
      )
    )
  }

  readonly DOWNLOAD = (
    resource: APIRequestResource,
    options: APIRequestOptions,
    fileName: string

  ) => {
    return this.tokenService.isRefreshing$.pipe(
      filter((isRefreshing) => !isRefreshing),
      switchMap(() =>
        this.http.get(generateUrl(resource, options), {
          params: options.params,
          responseType: 'blob',
          observe: 'response'
        })
      ),
      map(response => {
        // Extract filename from Content-Disposition header
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = fileName;

        if (contentDisposition) {
          // Try multiple patterns to extract filename
          let filenameMatch = null;

          // Pattern 1: filename*=UTF-8''filename or filename*=utf-8''filename
          filenameMatch = contentDisposition.match(/filename\*=(?:UTF-8|utf-8)?''([^;\r\n]*)/i);
          if (filenameMatch && filenameMatch[1]) {
            filename = decodeURIComponent(filenameMatch[1]);
          } else {
            // Pattern 2: filename="filename" or filename=filename
            filenameMatch = contentDisposition.match(/filename=(?:"([^"]*)"?|([^;\r\n]*))/i);
            if (filenameMatch) {
              filename = filenameMatch[1] || filenameMatch[2];
              // Clean up any remaining quotes
              filename = filename.replace(/^["']|["']$/g, '').trim();
            }
          }
        }

        if (filename && !filename.includes('.')) {

          const contentType = response.headers.get('Content-Type') || '';
          console.log(response);
          let extension = '';
          if (contentType.includes('pdf')) extension = '.pdf';
          else if (contentType.includes('image/jpeg') || contentType.includes('image/jpg')) extension = '.jpg';
          else if (contentType.includes('image/png')) extension = '.png';
          else if (contentType.includes('zip')) extension = '.zip';
          // No else needed here

          console.log('Content-Type:', contentType);
          console.log('Extension:', extension);

          filename += extension;
        }
        return {
          blob: response.body as Blob,
          filename: filename
        };
      })
    );
  }

  readonly DELETE = <T>(
    resource: APIRequestResource,
    data: unknown,
    options: APIRequestOptions,
    caching = false,
    forceRefresh = false,
    cachingTTL = CACHING_DEFAULT_TTL
  ) => {
    return this.tokenService.isRefreshing$.pipe(
      filter((isRefreshing) => !isRefreshing),
      switchMap(() =>
        this.http.delete<APIResponse<T>>(generateUrl(resource, options), {
          params: options.params,
          body: data,
          context: new HttpContext().set(CACHING_ENABLED, caching).set(CACHING_TTL, cachingTTL).set(CACHE_FORCE_REFRESH, forceRefresh),
        })
      )
    )
  }

  readonly PATCH = <T>(
    resource: APIRequestResource,
    data: unknown,
    options: APIRequestOptions,
    caching = false,
    forceRefresh = false,
    cachingTTL = CACHING_DEFAULT_TTL
  ) => {
    return this.tokenService.isRefreshing$.pipe(
      filter((isRefreshing) => !isRefreshing),
      switchMap(() =>
        this.http.patch<APIResponse<T>>(generateUrl(resource, options), data, {
          params: options.params,
          context: new HttpContext().set(CACHING_ENABLED, caching).set(CACHING_TTL, cachingTTL).set(CACHE_FORCE_REFRESH, forceRefresh),
        })
      )
    )
  }
  readonly POST = <T>(
    resource: APIRequestResource,
    data: unknown,
    options: APIRequestOptions,
    caching = false,
    forceRefresh = false,
    cachingTTL = CACHING_DEFAULT_TTL
  ) => {
    return this.tokenService.isRefreshing$.pipe(
      filter((isRefreshing) => !isRefreshing),
      switchMap(() => {
        // If data is FormData, don't set Content-Type header
        const headers: Record<string, string> = {};
        if (!(data instanceof FormData)) {
          headers['Content-Type'] = 'application/json';
        }

        return this.http.post<APIResponse<T>>(generateUrl(resource, options), data, {
          headers,
          params: options.params,
          context: new HttpContext()
            .set(CACHING_ENABLED, caching)
            .set(CACHING_TTL, cachingTTL)
            .set(CACHE_FORCE_REFRESH, forceRefresh),
        });
      })
    );
  }

  readonly PUT = <T>(
    resource: APIRequestResource,
    data: unknown,
    options: APIRequestOptions,
    caching = false,
    forceRefresh = false,
    cachingTTL = CACHING_DEFAULT_TTL
  ) => {
    return this.tokenService.isRefreshing$.pipe(
      filter((isRefreshing) => !isRefreshing),
      switchMap(() =>
        this.http.put<APIResponse<T>>(generateUrl(resource, options), data, {
          params: options.params,
          context: new HttpContext().set(CACHING_ENABLED, caching).set(CACHING_TTL, cachingTTL).set(CACHE_FORCE_REFRESH, forceRefresh),
        })
      )
    )
  }
}
